import { SimpleFormControl } from "../form/SimpleFormControl";
import { SimpleFormTextarea } from "../form/SimpleFormTextarea";
import { SimpleFormCheck } from "../form/SimpleFormCheck";
import { SimpleFormSelect, SimpleFormSelectOption } from "../form/SimpleFormSelect";
import SimpleFormTagsInput from "../form/SimpleFormTagsInput";
import { Col, Container, Form, Row } from "react-bootstrap";
import { useIntl } from "react-intl";
import { useField } from "formik";
import { useEffect } from "react";
import { useAuth } from "src/app/utils/useAuth";
import { CheckRoles } from "src/app/utils/CheckRoles";
import { UserModel } from "src/app/types/UserModel";
import { UserRole } from "src/app/transports/user";
import { WritingType } from "src/app/types/orders/order";
import { includes } from "lodash";
import moment from "moment";

export interface IUpsertOrderFormBody {
    manager: UserModel;
    moneySiteOptions?: Array<SimpleFormSelectOption>;
    thematicOptions?: Array<SimpleFormSelectOption>;
    contentTypeOptions?: Array<SimpleFormSelectOption>;
    writingTypeOptions?: Array<SimpleFormSelectOption>;
    writerOptions?: Array<SimpleFormSelectOption>;
    RDOptions?: Array<SimpleFormSelectOption>;
    platformOptions?: Array<SimpleFormSelectOption>;
    proofreaderOptions?: Array<SimpleFormSelectOption>;
}

const UpsertOrderForm = (props: IUpsertOrderFormBody) => {
    const {
        manager,
        moneySiteOptions = [],
        thematicOptions = [],
        contentTypeOptions = [],
        writingTypeOptions = [],
        writerOptions = [],
        RDOptions = [],
        platformOptions = [],
        proofreaderOptions = [],
    } = props;
    const { formatMessage } = useIntl();
    const { currentUser } = useAuth();

    const [writerNeeded, , writerNeededHelper] = useField("writerNeeded");
    const [writingTypeField] = useField("writingType");
    const [, , writerIdHelper] = useField("writerId");
    const [moneySiteId] = useField("moneySiteId");
    const [, , rdHelper] = useField("rdId");
    const [optiField, , optiHelper] = useField("opti");
    const [links0, , links0Helpers] = useField("links.0");
    const [links1, , links1helpers] = useField("links.1");
    const [url] = useField("url");
    const [createdAt] = useField("createdAt");
    const [reviewNeeded] = useField("reviewNeeded");
    const [platformId] = useField("platformId");
    const [updatedAt] = useField("updatedAt");
    const [deliveredAt] = useField("deliveredAt");
    const [reviewedAt] = useField("reviewedAt");
    const [processedAt] = useField("processedAt");
    const [sentAt] = useField("sentAt");
    const [keywords, , keywordsHelper] = useField(`keywords`);

    const publishAllowedData =
        currentUser.role === UserRole.ROLE_ADMIN
            ? undefined
            : moment(createdAt.value).add(7, "d").format("YYYY-MM-DD");

    useEffect(() => {
        if (links0.value?.url === "" && links0.value?.anchor === "") {
            links0Helpers.setValue(links1.value);
            links1helpers.setValue({ url: "", anchor: "" });
        }
    }, [links0.value?.url, links0.value?.anchor]);

    useEffect(() => {
        // add word (cooc) to secund keyword
        if (keywords.value?.length >= 2 && !includes(keywords.value[1], "(cooc)")) {
            const auxKeyword: string[] = keywords.value;
            auxKeyword[1] = `${keywords.value[1]} (cooc)`;
            keywordsHelper.setValue(auxKeyword);
        }

        if (keywords.value?.length > 0 && !optiField.value) {
            optiHelper.setValue(true);
        } else if (keywords.value?.length === 0 && optiField.value) {
            optiHelper.setValue(false);
        }

        // if we remove 1st keyword, remove (cooc) from the 1st key word
        if (keywords.value?.length > 0 && includes(keywords.value[0], "(cooc)")) {
            const auxKeyword: string[] = keywords.value;
            auxKeyword[0] = keywords.value[0]?.replace("(cooc)", "");
            keywordsHelper.setValue(auxKeyword);
        }
    }, [keywords.value]);

    useEffect(() => {
        if (
            (writingTypeField.value === WritingType.AI_AUTO ||
                writingTypeField.value === WritingType.EXPIRED ||
                writingTypeField.value === WritingType.TRANSLATE) &&
            writerNeeded.value
        ) {
            writerNeededHelper.setValue(false);
            writerIdHelper.setValue(null);
        }
        if (
            writingTypeField.value === WritingType.WRITE &&
            !writerNeeded.value &&
            !platformId.value
        ) {
            writerNeededHelper.setValue(true);
        }
    }, [writingTypeField.value]);

    useEffect(() => {
        if (
            manager.role === UserRole.ROLE_BUSINESS_MANAGER ||
            manager.role === UserRole.ROLE_LEAD_BUSINESS_MANAGER
        ) {
            rdHelper.setValue(moneySiteId.value);
        }
    }, [moneySiteId.value]);

    return (
        <Container>
            <Row>
                <Col sm={4}>
                    <SimpleFormSelect
                        isClearable={false}
                        name="moneySiteId"
                        label={formatMessage({ id: "common.field.moneySite" })}
                        placeholder={formatMessage({ id: "common.field.moneySite" })}
                        values={moneySiteOptions}
                    />
                    {!CheckRoles.isLeadBussinessOrUnder(manager.role) && (
                        <SimpleFormSelect
                            name="rdId"
                            label={formatMessage({ id: "common.field.rd" })}
                            placeholder={formatMessage({ id: "common.field.rd" })}
                            values={RDOptions}
                        />
                    )}
                    {CheckRoles.isLeadBussinessOrUnder(manager.role) && (
                        <SimpleFormSelect
                            name="platformId"
                            label={formatMessage({ id: "common.field.platform" })}
                            placeholder={formatMessage({ id: "common.field.platform" })}
                            values={platformOptions}
                        />
                    )}
                    <Row>
                        <Col>
                            <SimpleFormSelect
                                isClearable={false}
                                name="contentType"
                                label={formatMessage({ id: "common.field.contentType" })}
                                placeholder={formatMessage({ id: "common.field.contentType" })}
                                values={contentTypeOptions}
                            />
                        </Col>
                        <Col>
                            <SimpleFormSelect
                                isClearable={false}
                                name="writingType"
                                label={formatMessage({ id: "common.field.writingType" })}
                                placeholder={formatMessage({ id: "common.field.writingType" })}
                                values={writingTypeOptions}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <SimpleFormControl
                                name="wordCount"
                                label={formatMessage({ id: "common.field.wordsCount" })}
                                placeholder={formatMessage({ id: "common.field.wordsCount" })}
                                type="number"
                                min="0"
                            />
                        </Col>
                        <Col>
                            <SimpleFormControl
                                name="orderPrice"
                                label={formatMessage({ id: "common.field.orderPrice" })}
                                placeholder={formatMessage({ id: "common.field.orderPrice" })}
                                type="number"
                                min="0"
                                step={0.01}
                            />
                        </Col>
                    </Row>

                    <SimpleFormSelect
                        isClearable={false}
                        name="thematicId"
                        label={formatMessage({ id: "common.field.thematic" })}
                        placeholder={formatMessage({ id: "common.field.thematic" })}
                        values={thematicOptions}
                    />
                    <Col>
                        <Row>
                            <Col sm={4}>
                                <span className="form-label">
                                    {`${formatMessage({ id: "common.field.publishedAt" })} : `}
                                </span>
                            </Col>
                            <Col sm={8}>
                                <SimpleFormControl
                                    min={publishAllowedData}
                                    name="publishedAt"
                                    type="date"
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col sm={4}>
                                <span className="form-label">
                                    {`${formatMessage({ id: "common.field.createdAt" })} :`}
                                </span>
                            </Col>
                            <Col sm={4}>{`${createdAt.value}`}</Col>
                        </Row>
                        <Row>
                            <Col sm={4}>
                                <span className="form-label">
                                    {`${formatMessage({ id: "common.field.updatedAt" })} : `}
                                </span>
                            </Col>
                            <Col sm={4}>{`${updatedAt.value}`}</Col>
                        </Row>
                        {deliveredAt.value && (
                            <Row>
                                <Col sm={4}>
                                    <span className="form-label">
                                        {`${formatMessage({ id: "common.field.deliveredAt" })} : `}
                                    </span>
                                </Col>
                                <Col sm={4}>{`${deliveredAt.value}`}</Col>
                            </Row>
                        )}
                        {reviewedAt.value && (
                            <Row>
                                <Col sm={4}>
                                    <span className="form-label">
                                        {`${formatMessage({ id: "common.field.reviewedAt" })} : `}
                                    </span>
                                </Col>
                                <Col sm={4}>{`${reviewedAt.value}`}</Col>
                            </Row>
                        )}
                        {processedAt.value && (
                            <Row>
                                <Col sm={4}>
                                    <span className="form-label">
                                        {`${formatMessage({ id: "common.field.processedAt" })} : `}
                                    </span>
                                </Col>
                                <Col sm={4}>{`${processedAt.value}`}</Col>
                            </Row>
                        )}
                        {sentAt.value && (
                            <Row>
                                <Col sm={4}>
                                    <span className="form-label">
                                        {`${formatMessage({ id: "common.field.sentAt" })} : `}
                                    </span>
                                </Col>
                                <Col sm={4}>{`${sentAt.value}`}</Col>
                            </Row>
                        )}
                    </Col>
                </Col>
                <Col sm={8}>
                    <Row>
                        <SimpleFormControl
                            name="title"
                            label={formatMessage({ id: "common.field.title" })}
                            placeholder={formatMessage({ id: "common.field.title" })}
                        />
                        <Row>
                            <Col sm={7}>
                                <SimpleFormControl
                                    name="links.0.url"
                                    label={formatMessage({ id: "common.field.url" })}
                                    placeholder={formatMessage({ id: "common.field.url" })}
                                />
                            </Col>
                            <Col sm={5}>
                                <SimpleFormControl
                                    name="links.0.anchor"
                                    label={formatMessage({ id: "common.field.anchor" })}
                                    placeholder={formatMessage({ id: "common.field.anchor" })}
                                />
                            </Col>
                        </Row>
                        <>
                            {(links0.value?.url !== "" || links0.value?.anchor !== "") &&
                                (links0.value?.url != undefined ||
                                    links0.value?.anchor != undefined) && (
                                    <Row>
                                        <Col sm={7}>
                                            <SimpleFormControl
                                                name="links.1.url"
                                                placeholder={formatMessage({
                                                    id: "common.field.url",
                                                })}
                                            />
                                        </Col>
                                        <Col sm={5}>
                                            <SimpleFormControl
                                                disabled={!links0.value}
                                                name="links.1.anchor"
                                                placeholder={formatMessage({
                                                    id: "common.field.anchor",
                                                })}
                                            />
                                        </Col>
                                    </Row>
                                )}
                        </>
                        <SimpleFormTagsInput
                            name="keywords"
                            sorted={false}
                            label={formatMessage({
                                id: "common.field.keywords",
                            })}
                            tagPlaceholder={formatMessage({
                                id: "common.field.placeholder.addKeyword",
                            })}
                        />

                        <SimpleFormTextarea
                            name="commentForWriter"
                            label={formatMessage({ id: "common.field.commentForWriter" })}
                            placeholder={formatMessage({ id: "common.field.commentForWriter" })}
                            className="textarea"
                            type="textarea"
                            rows={4}
                        />
                        <SimpleFormTextarea
                            name="commentForLinker"
                            label={formatMessage({ id: "common.field.commentForLinker" })}
                            placeholder={formatMessage({ id: "common.field.commentForLinker" })}
                            className="textarea"
                            type="textarea"
                        />
                    </Row>
                    <Col>
                        <Row>
                            <Col sm={6}>
                                {writerNeeded.value ? (
                                    <SimpleFormSelect
                                        name="writerId"
                                        label={formatMessage({ id: "common.field.writer" })}
                                        placeholder={formatMessage({ id: "common.field.writer" })}
                                        values={writerOptions}
                                    />
                                ) : (
                                    <span className="form-label">
                                        {`${formatMessage({ id: "common.field.writer" })} : `}
                                    </span>
                                )}
                            </Col>
                            <Col sm={6}>
                                {reviewNeeded.value && (
                                    <SimpleFormSelect
                                        name="proofreaderId"
                                        label={formatMessage({ id: "common.field.proofreader" })}
                                        placeholder={formatMessage({
                                            id: "common.field.proofreader",
                                        })}
                                        values={proofreaderOptions}
                                    />
                                )}
                            </Col>
                        </Row>
                        <Row>
                            {CheckRoles.isManager(currentUser.role) && (
                                <Col sm={4}>
                                    <SimpleFormCheck
                                        name="approveNeeded"
                                        label={formatMessage({ id: "common.field.approveNeeded" })}
                                    />
                                </Col>
                            )}
                        </Row>
                    </Col>
                    {url.value && (
                        <Form.Label>
                            URL :{" "}
                            <a href={url.value} target="_blank">
                                {url.value}
                            </a>
                        </Form.Label>
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default UpsertOrderForm;
